/**
 * hive-kit: the Hive <-> Bee wire contract in code. Vendored verbatim into every repo
 * (hive, buzzbee, salesbee, lawbee, marketingbee, BuildBee) as `hive-kit.ts`; keep the copies identical and
 * change this original first. Runs on Cloudflare Workers and Node 22 (WebCrypto only, no deps).
 * Spec: HIVE_CONTRACT.md next to this file.
 *
 * Sections: wire types, request signing, SSO, and (2026-10-16) provider credentials: the PROVIDERS
 * catalog, AES-GCM sealing of stored keys, env/db resolution, per-isolate cache and live tests.
 */

export const HIVE_CONTRACT_VERSION = '1';

/** Max clock skew for signed requests and SSO tokens. */
export const MAX_SKEW_SECONDS = 300;
/** SSO tokens live this long. */
export const SSO_TTL_SECONDS = 60;

export type BeeId = 'buzzbee' | 'salesbee' | 'lawbee' | 'marketingbee' | 'buildbee';

export type HiveStatus = 'trialing' | 'active' | 'past_due' | 'inactive' | 'suspended';

export type HiveRole = 'owner' | 'admin' | 'member';

export type ProvisionRequest = {
  hiveOrgId: string;
  orgName: string;
  /** ISO 3166-1 alpha-2, 'CA' or 'US'. */
  country: string;
  /** Province/state code, e.g. 'ON'. Optional. */
  region?: string | null;
  timezone: string;
  currency: 'cad';
  plan: string;
  addons: string[];
  status: HiveStatus;
  trialEndsAt: string | null;
  owner: { hiveUserId: string; email: string; name: string };
};

export type ProvisionResponse = {
  beeWorkspaceId: string;
  /** Where the SSO launch lands after sign-in, relative to the Bee's web origin. */
  homePath: string;
  created: boolean;
};

export type EntitlementsRequest = {
  hiveOrgId: string;
  plan: string;
  addons: string[];
  status: HiveStatus;
  trialEndsAt: string | null;
  /** Catalog limits for the plan, after add-ons. Null value = unlimited. */
  limits: Record<string, number | null>;
  features: string[];
};

export type UsageResponse = {
  hiveOrgId: string;
  from: string;
  to: string;
  metrics: { metric: string; quantity: number; unit: string }[];
};

export type DeprovisionRequest = { hiveOrgId: string; mode: 'suspend' | 'delete' };

/** A user's state as Hive manages it. `removed` = no access to this Bee (membership deactivated). */
export type HiveUserStatus = 'active' | 'suspended' | 'removed';

/** `POST /hive/v1/users`: upsert one user in the org's workspace, matched by email. */
export type UserSyncRequest = {
  hiveOrgId: string;
  user: { hiveUserId: string; email: string; name: string; role: HiveRole; status: HiveUserStatus };
};

export type UserSyncResponse = { beeUserId: string; created: boolean; status: HiveUserStatus };

/** `GET /hive/v1/users?hiveOrgId=`: every user in the workspace, including ones created inside the Bee. */
export type BeeUser = {
  beeUserId: string;
  hiveUserId: string | null;
  email: string;
  name: string;
  /** The Bee's own role name (e.g. 'pm', 'lawyer', 'rep'). */
  role: string;
  status: 'active' | 'suspended' | 'removed' | 'invited';
  lastLoginAt: string | null;
};

export type UserListResponse = { hiveOrgId: string; users: BeeUser[] };

/** `POST /hive/v1/users/revoke-sessions` and `POST /hive/v1/users/password-reset`. */
export type UserActionRequest = { hiveOrgId: string; email: string };

/** Optional features a Bee implements beyond the base contract (absent = none of them). */
export type HiveCapability = 'users' | 'credentials' | 'import' | 'tick';

/**
 * `POST /hive/v1/tick` (capability 'tick', added 2026-10-23): Hive's every-minute cron starting a
 * Bee's scheduled jobs, for Bees that have no cron trigger of their own (Workers Free allows 5 per
 * account). Hive sends `Idempotency-Key: tick:<bee>:<scheduledTime>`.
 */
export const TICK_PATH = '/hive/v1/tick';
export type TickRequest = { scheduledTime: number };
export type TickResponse = { ok: true; [job: string]: unknown };

/**
 * `GET /hive/v1/workspaces` (capability 'import', added 2026-10-19): every workspace in the Bee,
 * including ones created inside it (standalone sign-ups) that Hive does not manage yet
 * (`hiveOrgId: null`). Hive's Import page adopts those with `POST /hive/v1/link`.
 */
export type BeeWorkspace = {
  beeWorkspaceId: string;
  name: string;
  /** ISO timestamp. */
  createdAt: string;
  /** Null: created inside the Bee and not managed by Hive yet. */
  hiveOrgId: string | null;
  /** The Bee's own plan id ('premium', 'trial', ...); Hive maps it to a catalog plan when it matches one. */
  plan: string | null;
  status: 'active' | 'suspended' | 'deleted';
  /** A Stripe subscription the Bee charges itself (standalone billing). Linking is refused while it is live. */
  standaloneSubscription: { status: string; currentPeriodEnd: string | null } | null;
  /** The first active owner (null when the workspace has none). */
  owner: { email: string; name: string } | null;
  /** Active and invited users. */
  userCount: number;
};

export type WorkspaceListResponse = { workspaces: BeeWorkspace[] };

/**
 * `POST /hive/v1/link`: hand an existing workspace to Hive. The Bee stamps `hiveOrgId` on it and
 * switches it to Hive-managed billing (from then on entitlements, users, SSO and deprovision work
 * as for a provisioned workspace). Idempotent: the same pair again answers `linked: false`.
 * 404 unknown workspace; 409 `already_linked` (the workspace belongs to another Hive org, or the
 * org already has another workspace in this Bee); 409 `standalone_subscription` (cancel the Bee's
 * own Stripe subscription first, so the client is never charged twice).
 */
export type LinkRequest = { hiveOrgId: string; beeWorkspaceId: string };
export type LinkResponse = { beeWorkspaceId: string; homePath: string; linked: boolean };

export type HealthResponse = {
  bee: BeeId;
  contract: string;
  version: string;
  plans: string[];
  /** Added 2026-10-16. Hive shows the provider-credentials screen only when this lists 'credentials'. */
  capabilities?: HiveCapability[];
};

export type SsoClaims = {
  iss: 'hive';
  aud: BeeId;
  sub: string; // hive user id
  email: string;
  name: string;
  hiveOrgId: string;
  role: HiveRole;
  iat: number;
  exp: number;
  jti: string;
};

const enc = new TextEncoder();

function toHex(buf: ArrayBuffer): string {
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function b64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64urlDecode(s: string): Uint8Array {
  const pad = s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4);
  const bin = atob(pad);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

async function hmac(secret: string, data: string): Promise<ArrayBuffer> {
  const key = await crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  return crypto.subtle.sign('HMAC', key, enc.encode(data));
}

function safeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/** The exact string that is signed: `${timestamp}.${METHOD}.${pathWithQuery}.${body}`. */
export function signingString(timestamp: string, method: string, pathWithQuery: string, body: string): string {
  return `${timestamp}.${method.toUpperCase()}.${pathWithQuery}.${body}`;
}

/** Headers Hive sends on every server-to-server call to a Bee. */
export async function signRequest(
  secret: string,
  method: string,
  pathWithQuery: string,
  body: string,
  now: number = Date.now(),
): Promise<Record<string, string>> {
  const ts = String(Math.floor(now / 1000));
  const sig = toHex(await hmac(secret, signingString(ts, method, pathWithQuery, body)));
  return {
    'X-Hive-Contract': HIVE_CONTRACT_VERSION,
    'X-Hive-Timestamp': ts,
    'X-Hive-Signature': `v1=${sig}`,
  };
}

/** Bee side: null when the request is authentic, otherwise the reason it is not. */
export async function verifyRequest(
  secret: string,
  method: string,
  pathWithQuery: string,
  body: string,
  headers: { get(name: string): string | null },
  now: number = Date.now(),
): Promise<string | null> {
  if (!secret) return 'hive secret not configured';
  const ts = headers.get('X-Hive-Timestamp');
  const sig = headers.get('X-Hive-Signature');
  if (!ts || !sig) return 'missing signature';
  const skew = Math.abs(Math.floor(now / 1000) - Number(ts));
  if (!Number.isFinite(skew) || skew > MAX_SKEW_SECONDS) return 'stale timestamp';
  const expected = `v1=${toHex(await hmac(secret, signingString(ts, method, pathWithQuery, body)))}`;
  return safeEqual(expected, sig) ? null : 'bad signature';
}

/** Hive side: a short-lived HS256 JWT the Bee exchanges at `GET /hive/sso?token=`. */
export async function createSsoToken(
  secret: string,
  claims: Omit<SsoClaims, 'iss' | 'iat' | 'exp' | 'jti'>,
  now: number = Date.now(),
): Promise<string> {
  const iat = Math.floor(now / 1000);
  const full: SsoClaims = { ...claims, iss: 'hive', iat, exp: iat + SSO_TTL_SECONDS, jti: crypto.randomUUID() };
  const head = b64url(enc.encode(JSON.stringify({ alg: 'HS256', typ: 'JWT' })));
  const body = b64url(enc.encode(JSON.stringify(full)));
  const sig = b64url(new Uint8Array(await hmac(secret, `${head}.${body}`)));
  return `${head}.${body}.${sig}`;
}

/**
 * Bee side: returns the claims, or throws. The caller MUST also reject a `jti` it has seen
 * before (store used jtis for SSO_TTL_SECONDS + MAX_SKEW_SECONDS).
 */
export async function verifySsoToken(secret: string, token: string, bee: BeeId, now: number = Date.now()): Promise<SsoClaims> {
  const parts = token.split('.');
  if (parts.length !== 3) throw new Error('malformed token');
  const [head, body, sig] = parts as [string, string, string];
  const expected = b64url(new Uint8Array(await hmac(secret, `${head}.${body}`)));
  if (!safeEqual(expected, sig)) throw new Error('bad token signature');
  const claims = JSON.parse(new TextDecoder().decode(b64urlDecode(body))) as SsoClaims;
  const t = Math.floor(now / 1000);
  if (claims.iss !== 'hive' || claims.aud !== bee) throw new Error('wrong audience');
  if (claims.exp + MAX_SKEW_SECONDS < t || claims.iat - MAX_SKEW_SECONDS > t) throw new Error('token expired');
  return claims;
}

// =================================================================================================
// Provider credentials (added 2026-10-16, still contract "1", additive). Spec: HIVE_CONTRACT.md,
// "Provider credentials". Hive super admins set each Bee's third-party keys; the Bee stores them
// sealed with AES-256-GCM in its own D1 and they win over the Bee's env vars. Never part of this
// channel: HIVE_SHARED_SECRET / BEE_<ID>_SECRET, CREDENTIALS_KEY, session or signing secrets.
// =================================================================================================

/** Who owns a credential set: a Bee, or Hive itself (Hive's own Stripe and Resend keys). */
export type CredentialOwner = BeeId | 'hive';

export type ProviderId =
  | 'anthropic'
  | 'stripe'
  | 'resend'
  | 'twilio'
  | 'elevenlabs'
  | 'hubspot'
  | 'google_places'
  | 'scout'
  | 'dnc_registry'
  | 'konekto_post'
  | 'konekto_post_check'
  | 'bluesky';

export type ProviderField = {
  /** The env var this field replaces; also its key in `fields` on the wire and in storage. */
  name: string;
  label: string;
  /** Secret fields are write-only: reads return only their last 4 characters. */
  secret: boolean;
  /** A stored set must contain every required field (the env fallback needs them too). */
  required: boolean;
  placeholder: string;
  /** Where to create or find the value; null for WRKRBZ-run services. */
  helpUrl: string | null;
  help?: string;
  /** `url`: http(s) URL (https only in production). `e164`: phone number like +14165550100. */
  format?: 'url' | 'e164';
  /** Accepted prefixes, checked on input (any one matches). */
  prefixes?: readonly string[];
};

export type ProviderSpec = {
  id: ProviderId;
  label: string;
  helpUrl: string | null;
  /** What this owner uses the provider for (shown in the admin console). */
  usedFor: string;
  /** What `POST .../test` checks. */
  testDescription: string;
  fields: readonly ProviderField[];
};

const fld = (f: ProviderField): ProviderField => f;

const FIELD = {
  ANTHROPIC_API_KEY: fld({ name: 'ANTHROPIC_API_KEY', label: 'API key', secret: true, required: true, placeholder: 'sk-ant-...', helpUrl: 'https://console.anthropic.com/settings/keys', prefixes: ['sk-ant-'] }),
  STRIPE_SECRET_KEY: fld({ name: 'STRIPE_SECRET_KEY', label: 'Secret key', secret: true, required: true, placeholder: 'sk_live_...', helpUrl: 'https://dashboard.stripe.com/apikeys', prefixes: ['sk_live_', 'sk_test_', 'rk_live_', 'rk_test_'] }),
  STRIPE_WEBHOOK_SECRET: fld({ name: 'STRIPE_WEBHOOK_SECRET', label: 'Webhook signing secret', secret: true, required: true, placeholder: 'whsec_...', helpUrl: 'https://dashboard.stripe.com/webhooks', prefixes: ['whsec_'] }),
  RESEND_API_KEY: fld({ name: 'RESEND_API_KEY', label: 'API key', secret: true, required: true, placeholder: 're_...', helpUrl: 'https://resend.com/api-keys', prefixes: ['re_'] }),
  TWILIO_ACCOUNT_SID: fld({ name: 'TWILIO_ACCOUNT_SID', label: 'Account SID', secret: false, required: true, placeholder: 'AC...', helpUrl: 'https://console.twilio.com/', prefixes: ['AC'] }),
  TWILIO_AUTH_TOKEN: fld({ name: 'TWILIO_AUTH_TOKEN', label: 'Auth token', secret: true, required: true, placeholder: '32 hex characters', helpUrl: 'https://console.twilio.com/' }),
  ELEVENLABS_API_KEY: fld({ name: 'ELEVENLABS_API_KEY', label: 'API key', secret: true, required: true, placeholder: 'sk_...', helpUrl: 'https://elevenlabs.io/app/settings/api-keys' }),
  ELEVENLABS_WEBHOOK_SECRET: fld({
    name: 'ELEVENLABS_WEBHOOK_SECRET',
    label: 'Webhook secret',
    secret: true,
    required: true,
    placeholder: 'wsec_...',
    helpUrl: 'https://elevenlabs.io/app/conversational-ai/settings',
    help: 'Verifies post-call webhooks. Required whenever VOICE_PROVIDER=elevenlabs.',
  }),
  HUBSPOT_ACCESS_TOKEN: fld({ name: 'HUBSPOT_ACCESS_TOKEN', label: 'Private app access token', secret: true, required: true, placeholder: 'pat-na1-...', helpUrl: 'https://developers.hubspot.com/docs/api/private-apps' }),
} as const;

const fromNumber = (name: string): ProviderField =>
  fld({
    name,
    label: 'SMS from number',
    secret: false,
    required: false,
    placeholder: '+14165550100',
    helpUrl: 'https://console.twilio.com/us1/develop/phone-numbers/manage/incoming',
    format: 'e164',
    help: 'A Twilio number on this account. Without it SMS is not sent.',
  });

const anthropic = (usedFor: string): ProviderSpec => ({
  id: 'anthropic',
  label: 'Anthropic (Claude)',
  helpUrl: 'https://console.anthropic.com/settings/keys',
  usedFor,
  testDescription: 'Lists one model (GET /v1/models).',
  fields: [FIELD.ANTHROPIC_API_KEY],
});

const stripe = (usedFor: string, extra: ProviderField[] = []): ProviderSpec => ({
  id: 'stripe',
  label: 'Stripe',
  helpUrl: 'https://dashboard.stripe.com/apikeys',
  usedFor,
  testDescription: 'Reads the account balance (GET /v1/balance). Webhook secrets cannot be checked live.',
  fields: [FIELD.STRIPE_SECRET_KEY, FIELD.STRIPE_WEBHOOK_SECRET, ...extra],
});

const resend = (usedFor: string): ProviderSpec => ({
  id: 'resend',
  label: 'Resend (email)',
  helpUrl: 'https://resend.com/api-keys',
  usedFor,
  testDescription: 'Lists sending domains (GET /domains); a sending-only key passes.',
  fields: [FIELD.RESEND_API_KEY],
});

const twilio = (usedFor: string, extra: ProviderField[] = []): ProviderSpec => ({
  id: 'twilio',
  label: 'Twilio',
  helpUrl: 'https://console.twilio.com/',
  usedFor,
  testDescription: 'Fetches the account (must be active) and, when set, checks the from number belongs to it.',
  fields: [FIELD.TWILIO_ACCOUNT_SID, FIELD.TWILIO_AUTH_TOKEN, ...extra],
});

const elevenlabs = (usedFor: string, extra: ProviderField[] = []): ProviderSpec => ({
  id: 'elevenlabs',
  label: 'ElevenLabs',
  helpUrl: 'https://elevenlabs.io/app/settings/api-keys',
  usedFor,
  testDescription: 'Fetches the user (GET /v1/user) and, when set, the agent and phone number.',
  fields: [FIELD.ELEVENLABS_API_KEY, ...extra, FIELD.ELEVENLABS_WEBHOOK_SECRET],
});

const hubspot = (usedFor: string): ProviderSpec => ({
  id: 'hubspot',
  label: 'HubSpot',
  helpUrl: 'https://developers.hubspot.com/docs/api/private-apps',
  usedFor,
  testDescription: 'Reads the account details (GET /account-info/v3/details).',
  fields: [FIELD.HUBSPOT_ACCESS_TOKEN],
});

/**
 * MarketingBee's admin connection check for Konekto Post (added 2026-10-23): a token for one
 * Konekto account (staging or production) that a super admin saves and tests from Hive. It is
 * never used to publish: every workspace connects its own token in Channels (CHANNEL_PROVIDERS,
 * which stay off this channel), hence its own id and field names.
 * The Bee's test is a full round trip that publishes nothing (accounts, token scope, a draft
 * created, read back and deleted).
 */
const konektoPostCheck = (): ProviderSpec => ({
  id: 'konekto_post_check',
  label: 'Konekto Post (connection check)',
  helpUrl: 'https://app.konekto.io',
  usedFor: 'Checking the Konekto Post integration end to end. Never used to publish; each workspace connects its own token.',
  testDescription: 'Lists the connected accounts, confirms the token is refused outside /api/social, creates a draft, reads it back through the adapter and deletes it. Nothing is published.',
  fields: [
    fld({ name: 'KONEKTO_CHECK_API_URL', label: 'API URL', secret: false, required: true, placeholder: 'https://api-staging.konekto.io', helpUrl: null, format: 'url' }),
    fld({
      name: 'KONEKTO_CHECK_TOKEN',
      label: 'Personal access token',
      secret: true,
      required: true,
      placeholder: 'konekto_pat_...',
      helpUrl: 'https://app.konekto.io',
      prefixes: ['konekto_pat_'],
      help: 'Konekto Post > Settings > Personal Access Tokens. Scoped to /api/social only.',
    }),
  ],
});

/**
 * Exactly which providers and fields each owner accepts, matching the env vars it reads today.
 * Field names are env var names and are unique per owner. Order = display order.
 */
export const PROVIDERS: Readonly<Record<CredentialOwner, readonly ProviderSpec[]>> = {
  buzzbee: [
    anthropic('Call summaries, intent detection and chat replies.'),
    stripe('Subscriptions for standalone (non-Hive) workspaces.'),
    elevenlabs('AI voice agent (VOICE_PROVIDER=elevenlabs).'),
    twilio('Phone numbers, voice (VOICE_PROVIDER=twilio) and SMS confirmations.', [fromNumber('TWILIO_FROM_NUMBER')]),
    resend('Sign-in links, notifications and owner emails.'),
    hubspot('CRM sync (ReceptionistBee).'),
  ],
  salesbee: [
    stripe('Subscriptions for standalone (non-Hive) workspaces.'),
    anthropic('Call summaries and outcome classification.'),
    resend('Sign-in links, invitations and sequence emails.'),
    {
      id: 'google_places',
      label: 'Google Places',
      helpUrl: 'https://console.cloud.google.com/google/maps-apis/credentials',
      usedFor: 'Prospect search (PROSPECT_PROVIDER=google_places).',
      testDescription: 'Runs a one-result Text Search that returns place ids only.',
      fields: [
        fld({
          name: 'GOOGLE_PLACES_API_KEY',
          label: 'API key',
          secret: true,
          required: true,
          placeholder: 'AIza...',
          helpUrl: 'https://console.cloud.google.com/google/maps-apis/credentials',
          help: 'Enable "Places API (New)" for this key.',
        }),
      ],
    },
    {
      id: 'scout',
      label: 'Scout prospecting service',
      helpUrl: null,
      usedFor: 'Prospect search and enrichment (PROSPECT_PROVIDER=scout).',
      testDescription: 'Calls GET {SCOUT_SERVICE_URL}/health with the service key.',
      fields: [
        fld({ name: 'SCOUT_SERVICE_URL', label: 'Service URL', secret: false, required: true, placeholder: 'https://scout.example.com', helpUrl: null, format: 'url' }),
        fld({ name: 'SCOUT_SERVICE_KEY', label: 'Service key', secret: true, required: true, placeholder: 'Bearer key', helpUrl: null }),
        fld({ name: 'SCOUT_WEBHOOK_SECRET', label: 'Webhook secret', secret: true, required: false, placeholder: 'Shared webhook secret', helpUrl: null, help: 'Verifies Scout result webhooks. Without it webhooks are refused.' }),
      ],
    },
    elevenlabs('AI outbound calls through an imported Twilio number (VOICE_PROVIDER=elevenlabs).', [
      fld({ name: 'ELEVENLABS_AGENT_ID', label: 'Agent id', secret: false, required: false, placeholder: 'agent_...', helpUrl: 'https://elevenlabs.io/app/conversational-ai/agents', help: 'Needed for AI calls.' }),
      fld({
        name: 'ELEVENLABS_PHONE_NUMBER_ID',
        label: 'Phone number id',
        secret: false,
        required: false,
        placeholder: 'phnum_...',
        helpUrl: 'https://elevenlabs.io/app/conversational-ai/phone-numbers',
        help: 'The imported Twilio number. Needed for AI calls.',
      }),
    ]),
    twilio('Click-to-call bridge, sequence SMS and status-callback signatures.'),
    {
      id: 'dnc_registry',
      label: 'Do-not-call registry lookup',
      helpUrl: null,
      usedFor: 'National DNC checks (CRTC DNCL, US National DNC). Unconfigured = calls that need a check are blocked.',
      testDescription: 'Looks up the fictional number +16135550100 (GET {DNC_REGISTRY_URL}/check).',
      fields: [
        fld({ name: 'DNC_REGISTRY_URL', label: 'Service URL', secret: false, required: true, placeholder: 'https://dnc.example.com', helpUrl: null, format: 'url' }),
        fld({ name: 'DNC_REGISTRY_API_KEY', label: 'API key', secret: true, required: true, placeholder: 'Bearer key', helpUrl: null }),
      ],
    },
    hubspot('CRM sync (Growth).'),
  ],
  lawbee: [
    stripe('Client payments through Stripe Connect, and subscriptions for standalone workspaces.', [
      fld({
        name: 'STRIPE_BILLING_WEBHOOK_SECRET',
        label: 'Billing webhook signing secret',
        secret: true,
        required: false,
        placeholder: 'whsec_...',
        helpUrl: 'https://dashboard.stripe.com/webhooks',
        prefixes: ['whsec_'],
        help: 'For /webhooks/stripe/billing (standalone subscriptions). The main secret is for /webhooks/stripe/payments.',
      }),
    ]),
    resend('Case updates, booking confirmations and sign-in emails.'),
    anthropic('Drafts and intake summaries (only for firms that turn AI on).'),
    twilio('SMS case updates (Growth).', [fromNumber('TWILIO_FROM')]),
  ],
  marketingbee: [
    stripe('Subscriptions for standalone (non-Hive) workspaces.'),
    anthropic('Content plans, drafts and brand-safety checks.'),
    resend('Sign-in links, approval digests and marketing email.'),
    konektoPostCheck(),
  ],
  // BuildBee has not adopted the credentials endpoints (its health lacks the 'credentials' capability).
  buildbee: [],
  hive: [stripe('All WRKRBZ subscription billing.'), resend('Hive sign-in, invitation and billing emails.')],
};

export type ChannelProviderId = Extract<ProviderId, 'konekto_post' | 'bluesky'>;

/**
 * Publishing channels a Bee's *workspaces* connect with their own accounts (MarketingBee). Never on
 * the Hive credentials channel: each workspace's values are sealed in the Bee's D1 with the
 * workspace id as `context`. Same field rules, `applyCredentialPatch` and `testProviderCredentials`.
 */
export const CHANNEL_PROVIDERS: Readonly<Record<ChannelProviderId, ProviderSpec>> = {
  konekto_post: {
    id: 'konekto_post',
    label: 'Konekto Post',
    helpUrl: 'https://app.konekto.io',
    usedFor: 'Publishing to Instagram, Facebook and LinkedIn through Konekto Post.',
    testDescription: 'Lists the connected social accounts (GET {KONEKTO_API_URL}/api/social/accounts).',
    fields: [
      fld({ name: 'KONEKTO_API_URL', label: 'API URL', secret: false, required: true, placeholder: 'https://api.konekto.io', helpUrl: null, format: 'url' }),
      fld({
        name: 'KONEKTO_POST_TOKEN',
        label: 'Personal access token',
        secret: true,
        required: true,
        placeholder: 'konekto_pat_...',
        helpUrl: 'https://app.konekto.io',
        prefixes: ['konekto_pat_'],
        help: 'Konekto Post > Settings > Personal Access Tokens. Scoped to /api/social only.',
      }),
    ],
  },
  bluesky: {
    id: 'bluesky',
    label: 'Bluesky',
    helpUrl: 'https://bsky.app/settings/app-passwords',
    usedFor: 'Publishing to a Bluesky account.',
    testDescription: 'Opens a session with the app password (POST com.atproto.server.createSession).',
    fields: [
      fld({ name: 'BLUESKY_HANDLE', label: 'Handle', secret: false, required: true, placeholder: 'brand.bsky.social', helpUrl: null }),
      fld({ name: 'BLUESKY_APP_PASSWORD', label: 'App password', secret: true, required: true, placeholder: 'xxxx-xxxx-xxxx-xxxx', helpUrl: 'https://bsky.app/settings/app-passwords' }),
    ],
  },
};

export const CREDENTIALS_PATH = '/hive/v1/credentials';

/** Signed path for one provider, e.g. `/hive/v1/credentials/twilio/test`. */
export function credentialPath(provider: ProviderId, action?: 'test'): string {
  return `${CREDENTIALS_PATH}/${provider}${action ? `/${action}` : ''}`;
}

export function providersFor(owner: CredentialOwner): readonly ProviderSpec[] {
  return PROVIDERS[owner];
}

/** The spec for `provider` if `owner` accepts it, else null (answer 404 `unknown_provider`). */
export function providerSpec(owner: CredentialOwner, provider: string): ProviderSpec | null {
  return PROVIDERS[owner].find((p) => p.id === provider) ?? null;
}

/** Field name -> value. Only fields with a value are present. */
export type CredentialValues = Record<string, string>;

export type CredentialSource = 'db' | 'env' | 'none';

export type CredentialActor = { hiveUserId: string; email: string };

/** `PUT /hive/v1/credentials/:provider`. */
export type CredentialPutRequest = {
  /**
   * Patch over the stored set: a string sets the field, null clears it, an omitted field keeps
   * its stored value. With no stored set the patch is the whole set. `{}` re-seals the stored set
   * with the current key (after a CREDENTIALS_KEY rotation).
   */
  fields: Record<string, string | null>;
  /** Optimistic concurrency: `version` from the last read (0 = nothing stored yet). */
  version: number;
  actor: CredentialActor;
};

/** `DELETE /hive/v1/credentials/:provider`: drop the stored set and fall back to env. */
export type CredentialDeleteRequest = { version: number; actor: CredentialActor };

/** `POST /hive/v1/credentials/:provider/test`. */
export type CredentialTestRequest = {
  actor: CredentialActor;
  /** Optional candidate patch (same rules as PUT) tested without saving; omitted = test what is in effect. */
  fields?: Record<string, string | null>;
};

export type CredentialFieldStatus = {
  name: string;
  set: boolean;
  /** Secret: its last 4 characters (null when shorter than 12). Non-secret: the full value. */
  hint: string | null;
};

export type CredentialTestResult = { ok: boolean; message: string; testedAt: string };

export type ProviderCredentialStatus = {
  provider: ProviderId;
  label: string;
  /** What the Bee uses right now: stored value, env fallback, or nothing (stub/disabled). */
  source: CredentialSource;
  /** Field state of the effective source; for `none`, whichever env fields are partially set. */
  fields: CredentialFieldStatus[];
  /** A stored set exists (even if it cannot be decrypted right now). */
  dbConfigured: boolean;
  /** The env vars hold a complete set: what DELETE reverts to. */
  envConfigured: boolean;
  /** Row version for optimistic concurrency; 0 when no row exists. */
  version: number;
  /** Key id that sealed the stored set. */
  keyId: string | null;
  /** Sealed with CREDENTIALS_KEY_PREVIOUS: a PUT with `fields: {}` re-seals it. */
  staleKey: boolean;
  /** Why a stored set is not in effect (e.g. undecryptable), else null. */
  problem: string | null;
  updatedAt: string | null;
  updatedByHiveUser: string | null;
  /** Cleared by every PUT/DELETE: it described the previous values. */
  lastTest: CredentialTestResult | null;
};

/** `GET /hive/v1/credentials`. Never contains a secret value. */
export type CredentialListResponse = {
  owner: CredentialOwner;
  storage: {
    /** False when CREDENTIALS_KEY is missing/weak in production: PUT/DELETE answer 503. */
    writable: boolean;
    problem: string | null;
    /** Id of the current sealing key (not secret). */
    keyId: string | null;
  };
  /** Other isolates pick up a change within this many seconds. */
  cacheTtlSeconds: number;
  providers: ProviderCredentialStatus[];
};

/** PUT and DELETE answer this (200). */
export type CredentialWriteResponse = {
  status: ProviderCredentialStatus;
  /** Field names whose value changed (never values). Empty for a re-seal or idempotent replay. */
  changed: string[];
};

export type CredentialTestResponse = {
  provider: ProviderId;
  source: 'db' | 'env' | 'candidate';
  ok: boolean;
  /** Human readable; never contains a secret value. */
  message: string;
  testedAt: string;
  latencyMs: number;
};

export type CredentialErrorCode =
  | 'invalid_request' // 400: bad JSON / schema
  | 'unknown_provider' // 404
  | 'version_conflict' // 409: `current` holds the latest status
  | 'invalid_fields' // 422: `fieldErrors` per field name
  | 'not_configured' // 422: test with nothing configured
  | 'storage_unavailable'; // 503: CREDENTIALS_KEY missing/weak in production

export type CredentialErrorResponse = {
  error: string;
  code: CredentialErrorCode;
  fieldErrors?: Record<string, string>;
  current?: ProviderCredentialStatus;
};

// ---- input validation ----------------------------------------------------------------------------

export const CREDENTIAL_VALUE_MAX_LENGTH = 4096;

export type CredentialPatchResult = { ok: true; values: CredentialValues; changed: string[] } | { ok: false; fieldErrors: Record<string, string> };

function fieldProblem(field: ProviderField, value: string, production: boolean): string | null {
  if (value.length > CREDENTIAL_VALUE_MAX_LENGTH) return `Longer than ${CREDENTIAL_VALUE_MAX_LENGTH} characters`;
  // eslint-disable-next-line no-control-regex
  if (/[\s\u0000-\u001f\u007f]/.test(value)) return 'Must not contain spaces or control characters';
  if (field.prefixes && !field.prefixes.some((p) => value.startsWith(p))) return `Expected to start with ${field.prefixes.join(' or ')}`;
  if (field.format === 'e164' && !/^\+[1-9]\d{6,14}$/.test(value)) return 'Expected a phone number like +14165550100';
  if (field.format === 'url') {
    let url: URL;
    try {
      url = new URL(value);
    } catch {
      return 'Expected a URL';
    }
    if (url.protocol !== 'https:' && (production || url.protocol !== 'http:')) return production ? 'Must be an https URL' : 'Must be an http(s) URL';
    if (url.username || url.password) return 'Must not contain a user name or password';
  }
  return null;
}

/**
 * Applies a PUT/test patch to the stored set (null = nothing stored). Strings are trimmed; `''` is
 * rejected (send null to clear). After merging, every required field must be present.
 */
export function applyCredentialPatch(
  spec: ProviderSpec,
  current: CredentialValues | null,
  patch: Readonly<Record<string, string | null>>,
  opts: { production: boolean },
): CredentialPatchResult {
  const fieldErrors: Record<string, string> = {};
  const next: CredentialValues = {};
  for (const f of spec.fields) {
    const v = current?.[f.name];
    if (v) next[f.name] = v;
  }
  for (const [name, raw] of Object.entries(patch)) {
    const field = spec.fields.find((f) => f.name === name);
    if (!field) {
      fieldErrors[name] = `Unknown field for ${spec.label}`;
      continue;
    }
    if (raw === null) {
      delete next[name];
      continue;
    }
    if (typeof raw !== 'string') {
      fieldErrors[name] = 'Must be a string or null';
      continue;
    }
    const value = raw.trim();
    if (!value) {
      fieldErrors[name] = 'Empty; send null to clear the field';
      continue;
    }
    const problem = fieldProblem(field, value, opts.production);
    if (problem) fieldErrors[name] = problem;
    else next[name] = value;
  }
  for (const f of spec.fields) if (f.required && !next[f.name] && !fieldErrors[f.name]) fieldErrors[f.name] = 'Required';
  if (Object.keys(fieldErrors).length) return { ok: false, fieldErrors };
  const changed = spec.fields.filter((f) => (current?.[f.name] ?? '') !== (next[f.name] ?? '')).map((f) => f.name);
  return { ok: true, values: next, changed };
}

// ---- resolution: stored value, else env, else unconfigured ---------------------------------------

type EnvLike = Readonly<Record<string, unknown>>;

/** Every field of `spec` that has a value in env (possibly an incomplete set). */
export function envFieldValues(spec: ProviderSpec, env: object): CredentialValues {
  const e = env as EnvLike;
  const out: CredentialValues = {};
  for (const f of spec.fields) {
    const v = e[f.name];
    if (typeof v === 'string' && v.trim()) out[f.name] = v.trim();
  }
  return out;
}

/** The env set for `spec` when all its required fields are present, else null. */
export function envCredentialValues(spec: ProviderSpec, env: object): CredentialValues | null {
  const values = envFieldValues(spec, env);
  return spec.fields.every((f) => !f.required || values[f.name]) ? values : null;
}

export type ResolvedProvider = { source: CredentialSource; values: CredentialValues };

/** Decrypted stored sets by provider (only providers whose row exists and decrypted). */
export type StoredCredentialSets = Partial<Record<ProviderId, CredentialValues>>;

/**
 * Per provider of `owner`: the stored set wins as a whole (env is never mixed into it), else the
 * env set if complete, else `none` with no values.
 */
export function resolveCredentials(owner: CredentialOwner, env: object, stored: StoredCredentialSets): Partial<Record<ProviderId, ResolvedProvider>> {
  const out: Partial<Record<ProviderId, ResolvedProvider>> = {};
  for (const spec of PROVIDERS[owner]) {
    const db = stored[spec.id];
    if (db) {
      out[spec.id] = { source: 'db', values: { ...db } };
      continue;
    }
    const fromEnv = envCredentialValues(spec, env);
    out[spec.id] = fromEnv ? { source: 'env', values: fromEnv } : { source: 'none', values: {} };
  }
  return out;
}

/**
 * The env the rest of the app should read: a copy of `env` with each stored set written over its
 * env vars (fields absent from the stored set are removed, so env never fills gaps). Returns `env`
 * itself when nothing is stored. Bindings (DB, R2...) are carried over untouched.
 */
export function applyCredentials<E extends object>(owner: CredentialOwner, env: E, stored: StoredCredentialSets): E {
  const specs = PROVIDERS[owner].filter((s) => stored[s.id]);
  if (!specs.length) return env;
  const out: Record<string, unknown> = { ...(env as Record<string, unknown>) };
  for (const spec of specs) {
    const values = stored[spec.id] ?? {};
    for (const f of spec.fields) {
      const v = values[f.name];
      if (v) out[f.name] = v;
      else delete out[f.name];
    }
  }
  return out as E;
}

/** Secret: last 4 characters when the value has at least 12 (else null). Non-secret: the value. */
export function credentialHint(field: ProviderField, value: string | null | undefined): string | null {
  if (!value) return null;
  if (!field.secret) return value;
  return value.length >= 12 ? value.slice(-4) : null;
}

/** Field name -> hint for every field with a value: the `field_hints` column and audit details. */
export function credentialHints(spec: ProviderSpec, values: CredentialValues): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const f of spec.fields) if (values[f.name]) out[f.name] = credentialHint(f, values[f.name]);
  return out;
}

/** A `provider_credential` row as the Bee read it (dates as Date, ISO string or epoch ms). */
export type StoredCredentialRow = {
  provider: string;
  ciphertext: string | null;
  keyVersion: string | null;
  version: number;
  updatedAt: Date | string | number | null;
  updatedByHiveUser: string | null;
  lastTestAt: Date | string | number | null;
  lastTestOk: boolean | null;
  lastTestMessage: string | null;
};

function isoOrNull(v: Date | string | number | null): string | null {
  if (v === null || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * The wire status of one provider. `dbValues` is the decrypted stored set (null when there is
 * none, or when it could not be opened: then pass the reason as `dbProblem`).
 */
export function credentialStatus(
  spec: ProviderSpec,
  input: { env: object; row: StoredCredentialRow | null; dbValues: CredentialValues | null; dbProblem: string | null; currentKeyId: string | null },
): ProviderCredentialStatus {
  const { row, dbValues } = input;
  const envSet = envCredentialValues(spec, input.env);
  const source: CredentialSource = dbValues ? 'db' : envSet ? 'env' : 'none';
  const values = dbValues ?? envSet ?? envFieldValues(spec, input.env);
  const stored = row?.ciphertext ? row : null;
  const testedAt = row ? isoOrNull(row.lastTestAt) : null;
  return {
    provider: spec.id,
    label: spec.label,
    source,
    fields: spec.fields.map((f) => ({ name: f.name, set: !!values[f.name], hint: credentialHint(f, values[f.name]) })),
    dbConfigured: !!stored,
    envConfigured: !!envSet,
    version: row?.version ?? 0,
    keyId: stored?.keyVersion ?? null,
    staleKey: !!stored?.keyVersion && !!input.currentKeyId && stored.keyVersion !== input.currentKeyId,
    problem: input.dbProblem,
    updatedAt: row ? isoOrNull(row.updatedAt) : null,
    updatedByHiveUser: row?.updatedByHiveUser ?? null,
    lastTest: row && testedAt ? { ok: !!row.lastTestOk, message: row.lastTestMessage ?? '', testedAt } : null,
  };
}

// ---- sealing: HKDF-SHA256 -> AES-256-GCM ----------------------------------------------------------

export const CREDENTIALS_KEY_MIN_LENGTH = 32;

/** Key material: CREDENTIALS_KEY, plus CREDENTIALS_KEY_PREVIOUS during a rotation. */
export type CredentialKeyring = { current: string; previous: string | null };

/**
 * The keyring from the owner's env, or the problem that makes stored credentials unavailable.
 * Production: CREDENTIALS_KEY must be set, 32+ characters, not `dev-...`. Elsewhere an unset key
 * falls back to a fixed, insecure dev key so local setups work with no configuration.
 */
export function credentialKeyring(
  owner: CredentialOwner,
  input: { key: string | null | undefined; previous?: string | null; production: boolean },
): { keyring: CredentialKeyring | null; problem: string | null } {
  const key = input.key?.trim() || null;
  const previous = input.previous?.trim() || null;
  if (!key) {
    if (input.production) return { keyring: null, problem: 'CREDENTIALS_KEY is not set: stored credentials are unavailable and env values are used' };
    return { keyring: { current: `dev-insecure-credentials-key:${owner}`, previous }, problem: null };
  }
  if (input.production && (key.length < CREDENTIALS_KEY_MIN_LENGTH || key.toLowerCase().startsWith('dev-'))) {
    return { keyring: null, problem: `CREDENTIALS_KEY is too weak for production (${CREDENTIALS_KEY_MIN_LENGTH}+ characters, not dev-...)` };
  }
  return { keyring: { current: key, previous: previous && previous !== key ? previous : null }, problem: null };
}

export class CredentialKeyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CredentialKeyError';
  }
}

type DerivedKey = { kid: string; key: CryptoKey };
/** Per-isolate cache of derived keys: HKDF runs once per key material, not per request. */
const derivedKeys = new Map<string, DerivedKey>();

async function deriveCredentialKey(owner: CredentialOwner, material: string): Promise<DerivedKey> {
  const cacheKey = `${owner}\u0000${material}`;
  const hit = derivedKeys.get(cacheKey);
  if (hit) return hit;
  const ikm = await crypto.subtle.importKey('raw', enc.encode(material), 'HKDF', false, ['deriveKey', 'deriveBits']);
  const salt = enc.encode(`wrkrbz-credentials:${owner}`);
  const key = await crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt, info: enc.encode('wrkrbz-credentials/v1/aes-256-gcm') },
    ikm,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
  const kidBits = await crypto.subtle.deriveBits({ name: 'HKDF', hash: 'SHA-256', salt, info: enc.encode('wrkrbz-credentials/v1/key-id') }, ikm, 64);
  const derived = { kid: toHex(kidBits), key };
  if (derivedKeys.size >= 16) derivedKeys.clear();
  derivedKeys.set(cacheKey, derived);
  return derived;
}

/** `context` (e.g. a workspace id for per-workspace channel credentials) is appended when given. */
const credentialAad = (owner: CredentialOwner, provider: string, context?: string) =>
  enc.encode(`wrkrbz-credentials:v1:${owner}:${provider}${context ? `:${context}` : ''}`);

/** Id of the key that seals new values (16 hex characters; safe to show and store). */
export async function credentialKeyId(owner: CredentialOwner, keyring: CredentialKeyring): Promise<string> {
  return (await deriveCredentialKey(owner, keyring.current)).kid;
}

/**
 * Encrypts a set for the `ciphertext` column as `v1.<keyId>.<iv>.<ciphertext>` (base64url). The
 * AAD binds it to owner and provider (and `context`, when given), so a blob copied to another row,
 * workspace or Bee does not open.
 */
export async function sealCredentials(
  owner: CredentialOwner,
  keyring: CredentialKeyring,
  provider: ProviderId,
  values: CredentialValues,
  context?: string,
): Promise<{ ciphertext: string; keyId: string }> {
  const { kid, key } = await deriveCredentialKey(owner, keyring.current);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: credentialAad(owner, provider, context) }, key, enc.encode(JSON.stringify(values)));
  return { ciphertext: `v1.${kid}.${b64url(iv)}.${b64url(new Uint8Array(ct))}`, keyId: kid };
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const out = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(out).set(bytes);
  return out;
}

/** Decrypts a `ciphertext` column with the current or previous key. Throws CredentialKeyError. */
export async function openCredentials(
  owner: CredentialOwner,
  keyring: CredentialKeyring,
  provider: ProviderId,
  ciphertext: string,
  context?: string,
): Promise<CredentialValues> {
  const parts = ciphertext.split('.');
  if (parts.length !== 4 || parts[0] !== 'v1') throw new CredentialKeyError('stored credentials have an unknown format');
  const [, kid, ivPart, ctPart] = parts as [string, string, string, string];
  let derived: DerivedKey | null = null;
  for (const material of [keyring.current, keyring.previous]) {
    if (!material) continue;
    const d = await deriveCredentialKey(owner, material);
    if (d.kid === kid) {
      derived = d;
      break;
    }
  }
  if (!derived) throw new CredentialKeyError(`stored credentials were sealed with key ${kid}, which is neither CREDENTIALS_KEY nor CREDENTIALS_KEY_PREVIOUS`);
  let plain: ArrayBuffer;
  try {
    plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: toArrayBuffer(b64urlDecode(ivPart)), additionalData: credentialAad(owner, provider, context) },
      derived.key,
      toArrayBuffer(b64urlDecode(ctPart)),
    );
  } catch {
    throw new CredentialKeyError('stored credentials failed to decrypt (tampered with, or sealed for another provider)');
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(plain));
  } catch {
    throw new CredentialKeyError('stored credentials are not valid JSON');
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new CredentialKeyError('stored credentials are not an object');
  const out: CredentialValues = {};
  for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) if (typeof v === 'string') out[k] = v;
  return out;
}

// ---- per-isolate cache ---------------------------------------------------------------------------

/** How long an isolate trusts its decrypted credentials before re-reading D1. */
export const CREDENTIALS_CACHE_TTL_MS = 60_000;

/**
 * Per-isolate cache of the decrypted stored sets. Keep one at module scope and `invalidate()` it
 * after every write in this isolate; other isolates refresh within the TTL. Deliberately no shared
 * in-flight promise: on Workers a promise started by one request must not be awaited by another.
 */
export class CredentialCache<T = StoredCredentialSets> {
  private entry: { data: T; at: number; generation: number } | null = null;
  private generation = 0;

  constructor(
    private readonly ttlMs: number = CREDENTIALS_CACHE_TTL_MS,
    private readonly now: () => number = () => Date.now(),
  ) {}

  async get(load: () => Promise<T>): Promise<T> {
    const e = this.entry;
    if (e && e.generation === this.generation && this.now() - e.at < this.ttlMs) return e.data;
    const generation = this.generation;
    const data = await load();
    // A write that invalidated meanwhile wins: never cache what was read before it.
    if (generation === this.generation) this.entry = { data, at: this.now(), generation };
    return data;
  }

  invalidate(): void {
    this.generation++;
    this.entry = null;
  }
}

// ---- live provider tests -------------------------------------------------------------------------

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export type ProviderTestOutcome = { ok: boolean; message: string; latencyMs: number };

export const PROVIDER_TEST_TIMEOUT_MS = 8_000;
/** Fictional (555-01xx) number the DNC registry test looks up. */
export const DNC_TEST_NUMBER = '+16135550100';

type Probe = { status: number; body: unknown; text: string };

class ProbeError extends Error {}

const asRecord = (b: unknown): Record<string, unknown> => (b && typeof b === 'object' && !Array.isArray(b) ? (b as Record<string, unknown>) : {});

function providerMessage(body: unknown, text: string): string {
  const b = asRecord(body);
  const e = b.error;
  if (typeof asRecord(e).message === 'string') return asRecord(e).message as string;
  if (typeof e === 'string') return e;
  if (typeof b.message === 'string') return b.message;
  const d = b.detail;
  if (typeof asRecord(d).message === 'string') return asRecord(d).message as string;
  if (typeof d === 'string') return d;
  return body === null ? text.trim().slice(0, 200) : '';
}

/** Names of fields that are not secret anywhere in the catalog; every other value is redacted. */
const PUBLIC_FIELD_NAMES = new Set(
  Object.values(PROVIDERS)
    .flatMap((specs) => specs.flatMap((s) => s.fields))
    .filter((f) => !f.secret)
    .map((f) => f.name),
);

function redact(message: string, values: CredentialValues): string {
  let out = message;
  for (const [name, v] of Object.entries(values)) if (!PUBLIC_FIELD_NAMES.has(name) && v.length >= 4) out = out.split(v).join('[redacted]');
  return out.length > 240 ? `${out.slice(0, 237)}...` : out;
}

/**
 * One cheap, read-only live call per provider that proves the credentials work (two for Twilio
 * with a from number, up to three for ElevenLabs with agent/number ids). Network errors and
 * timeouts are failures. Messages never contain a secret value. Inject `fetch` in tests.
 */
export async function testProviderCredentials(
  provider: ProviderId,
  values: CredentialValues,
  opts: { fetch?: FetchLike; timeoutMs?: number; now?: () => number } = {},
): Promise<ProviderTestOutcome> {
  const doFetch: FetchLike = opts.fetch ?? ((i, init) => fetch(i, init));
  const now = opts.now ?? (() => Date.now());
  const timeoutMs = opts.timeoutMs ?? PROVIDER_TEST_TIMEOUT_MS;
  const started = now();
  const v = (name: string) => values[name] ?? '';

  const probe = async (url: string, init: RequestInit = {}): Promise<Probe> => {
    let res: Response;
    try {
      res = await doFetch(url, { ...init, signal: AbortSignal.timeout(timeoutMs) });
    } catch (err) {
      const timedOut = err instanceof Error && (err.name === 'TimeoutError' || err.name === 'AbortError');
      const reason = timedOut ? `no answer within ${Math.round(timeoutMs / 1000)} s` : err instanceof Error ? err.message : String(err);
      let host = url;
      try {
        host = new URL(url).host;
      } catch {
        /* keep the raw url */
      }
      throw new ProbeError(`Could not reach ${host} (${reason}).`);
    }
    const text = await res.text();
    let body: unknown;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = null;
    }
    return { status: res.status, body, text };
  };
  const failure = (label: string, p: Probe) => {
    const detail = providerMessage(p.body, p.text) || 'no details';
    return p.status === 401 || p.status === 403 ? `${label} rejected the credentials (HTTP ${p.status}): ${detail}` : `${label} answered HTTP ${p.status}: ${detail}`;
  };

  const run = async (): Promise<{ ok: boolean; message: string }> => {
    switch (provider) {
      case 'anthropic': {
        const p = await probe('https://api.anthropic.com/v1/models?limit=1', { headers: { 'x-api-key': v('ANTHROPIC_API_KEY'), 'anthropic-version': '2023-06-01' } });
        return p.status === 200 ? { ok: true, message: 'Anthropic accepted the key.' } : { ok: false, message: failure('Anthropic', p) };
      }
      case 'stripe': {
        const p = await probe('https://api.stripe.com/v1/balance', { headers: { Authorization: `Bearer ${v('STRIPE_SECRET_KEY')}` } });
        if (p.status !== 200) return { ok: false, message: failure('Stripe', p) };
        const mode = asRecord(p.body).livemode === true ? 'live mode' : 'test mode';
        return { ok: true, message: `Stripe accepted the key (${mode}). Webhook secrets cannot be checked live.` };
      }
      case 'resend': {
        const p = await probe('https://api.resend.com/domains', { headers: { Authorization: `Bearer ${v('RESEND_API_KEY')}` } });
        if (p.status === 200) {
          const data = asRecord(p.body).data;
          const list = Array.isArray(data) ? data : [];
          const verified = list.filter((d) => asRecord(d).status === 'verified').length;
          return { ok: true, message: `Resend accepted the key (${list.length} domain${list.length === 1 ? '' : 's'}, ${verified} verified).` };
        }
        if (p.status === 401 && asRecord(p.body).name === 'restricted_api_key') return { ok: true, message: 'Resend accepted the key (sending-only key, so domains were not listed).' };
        return { ok: false, message: failure('Resend', p) };
      }
      case 'twilio': {
        const sid = v('TWILIO_ACCOUNT_SID');
        const headers = { Authorization: `Basic ${btoa(`${sid}:${v('TWILIO_AUTH_TOKEN')}`)}` };
        const base = `https://api.twilio.com/2010-04-01/Accounts/${encodeURIComponent(sid)}`;
        const p = await probe(`${base}.json`, { headers });
        if (p.status !== 200) return { ok: false, message: failure('Twilio', p) };
        const status = String(asRecord(p.body).status ?? 'unknown');
        if (status !== 'active') return { ok: false, message: `The Twilio account is ${status}, not active.` };
        const from = values.TWILIO_FROM_NUMBER ?? values.TWILIO_FROM;
        if (!from) return { ok: true, message: 'Twilio accepted the credentials (account active).' };
        const n = await probe(`${base}/IncomingPhoneNumbers.json?${new URLSearchParams({ PhoneNumber: from, PageSize: '1' }).toString()}`, { headers });
        if (n.status !== 200) return { ok: false, message: failure('Twilio', n) };
        const numbers = asRecord(n.body).incoming_phone_numbers;
        return Array.isArray(numbers) && numbers.length > 0
          ? { ok: true, message: `Twilio accepted the credentials (account active, ${from} is on it).` }
          : { ok: false, message: `${from} is not a number on this Twilio account.` };
      }
      case 'elevenlabs': {
        const headers = { 'xi-api-key': v('ELEVENLABS_API_KEY') };
        const p = await probe('https://api.elevenlabs.io/v1/user', { headers });
        // A key scoped without user_read still authenticates; ElevenLabs says so in detail.status.
        const scoped = p.status === 401 && asRecord(asRecord(p.body).detail).status === 'missing_permissions';
        if (p.status !== 200 && !scoped) return { ok: false, message: failure('ElevenLabs', p) };
        const notes: string[] = scoped ? ['key without user_read permission'] : [];
        const lookups = [
          ['ELEVENLABS_AGENT_ID', 'agents', 'agent'],
          ['ELEVENLABS_PHONE_NUMBER_ID', 'phone-numbers', 'phone number'],
        ] as const;
        for (const [name, path, label] of lookups) {
          const id = values[name];
          if (!id) continue;
          const r = await probe(`https://api.elevenlabs.io/v1/convai/${path}/${encodeURIComponent(id)}`, { headers });
          if (r.status === 404) return { ok: false, message: `ElevenLabs has no ${label} ${id} on this account.` };
          if (r.status !== 200) return { ok: false, message: failure('ElevenLabs', r) };
          notes.push(`${label} found`);
        }
        return { ok: true, message: `ElevenLabs accepted the key${notes.length ? ` (${notes.join(', ')})` : ''}.` };
      }
      case 'hubspot': {
        const p = await probe('https://api.hubapi.com/account-info/v3/details', { headers: { Authorization: `Bearer ${v('HUBSPOT_ACCESS_TOKEN')}` } });
        if (p.status !== 200) return { ok: false, message: failure('HubSpot', p) };
        const portal = asRecord(p.body).portalId;
        return { ok: true, message: `HubSpot accepted the token${portal !== undefined ? ` (portal ${String(portal)})` : ''}.` };
      }
      case 'google_places': {
        const p = await probe('https://places.googleapis.com/v1/places:searchText', {
          method: 'POST',
          headers: { 'content-type': 'application/json', 'X-Goog-Api-Key': v('GOOGLE_PLACES_API_KEY'), 'X-Goog-FieldMask': 'places.id' },
          body: JSON.stringify({ textQuery: 'coffee in Toronto', pageSize: 1 }),
        });
        return p.status === 200 ? { ok: true, message: 'Google Places accepted the key.' } : { ok: false, message: failure('Google Places', p) };
      }
      case 'scout': {
        const p = await probe(`${v('SCOUT_SERVICE_URL').replace(/\/+$/, '')}/health`, { headers: { Authorization: `Bearer ${v('SCOUT_SERVICE_KEY')}` } });
        return p.status >= 200 && p.status < 300 ? { ok: true, message: 'Scout answered its health check with the key.' } : { ok: false, message: failure('Scout', p) };
      }
      case 'konekto_post': {
        const p = await probe(`${v('KONEKTO_API_URL').replace(/\/+$/, '')}/api/social/accounts`, { headers: { Authorization: `Bearer ${v('KONEKTO_POST_TOKEN')}` } });
        if (p.status !== 200) return { ok: false, message: failure('Konekto Post', p) };
        const data = asRecord(p.body).data;
        const n = Array.isArray(data) ? data.length : 0;
        return { ok: true, message: `Konekto Post accepted the token (${n} connected account${n === 1 ? '' : 's'}).` };
      }
      case 'konekto_post_check': {
        // Hive-side fallback only; MarketingBee runs the full round trip for this provider.
        const p = await probe(`${v('KONEKTO_CHECK_API_URL').replace(/\/+$/, '')}/api/social/accounts`, { headers: { Authorization: `Bearer ${v('KONEKTO_CHECK_TOKEN')}` } });
        return p.status === 200 ? { ok: true, message: 'Konekto Post accepted the token.' } : { ok: false, message: failure('Konekto Post', p) };
      }
      case 'bluesky': {
        const p = await probe('https://bsky.social/xrpc/com.atproto.server.createSession', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ identifier: v('BLUESKY_HANDLE'), password: v('BLUESKY_APP_PASSWORD') }),
        });
        return p.status === 200 ? { ok: true, message: `Bluesky accepted the app password for ${v('BLUESKY_HANDLE')}.` } : { ok: false, message: failure('Bluesky', p) };
      }
      case 'dnc_registry': {
        const q = new URLSearchParams({ country: 'CA', phone: DNC_TEST_NUMBER }).toString();
        const p = await probe(`${v('DNC_REGISTRY_URL').replace(/\/+$/, '')}/check?${q}`, { headers: { Authorization: `Bearer ${v('DNC_REGISTRY_API_KEY')}` } });
        if (p.status !== 200) return { ok: false, message: failure('The DNC registry', p) };
        const listed = asRecord(p.body).listed;
        return typeof listed === 'boolean'
          ? { ok: true, message: `The DNC registry answered (test number listed: ${String(listed)}).` }
          : { ok: false, message: 'The DNC registry answered without a boolean "listed" field.' };
      }
    }
  };

  let result: { ok: boolean; message: string };
  try {
    result = await run();
  } catch (err) {
    result = { ok: false, message: err instanceof ProbeError ? err.message : `Test failed: ${err instanceof Error ? err.message : String(err)}` };
  }
  return { ok: result.ok, message: redact(result.message, values), latencyMs: Math.max(0, now() - started) };
}
